import tradeDataStore from "@/store/tradeDataStore";

/**
 * Pure helper — finds a CP by executionRequestId on an already-resolved trade
 * object. Intended for React renderers that subscribe to tradeDataStore via hook
 * and pass thisTrade in directly, avoiding a second store read.
 */
export function findCPOnTrade(
  trade: any,
  executionRequestId: number | string | undefined,
): any | undefined {
  if (executionRequestId === undefined || executionRequestId === null) {
    return undefined;
  }
  return trade?.executionRequestCounterParties?.find(
    (cp: any) => cp.executionRequestId == executionRequestId,
  );
}

/**
 * Pure helper — finds the CP that contains a leg matching blockId.
 * Covers the blockId-based lookup pattern used in structure trades.
 */
export function findCPOnTradeByBlockId(
  trade: any,
  blockId: number | string | undefined,
): any | undefined {
  if (blockId === undefined || blockId === null) {
    return undefined;
  }
  return trade?.executionRequestCounterParties?.find((cp: any) =>
    cp.legs?.find((leg: any) => leg.blockId == blockId),
  );
}

/**
 * Looks up the live CP from the Zustand tradeDataStore by esid +
 * executionRequestId. Returns undefined when no match — caller falls back to
 * params.data for display.
 *
 * Uses `tradeDataStore.getState()` for direct access — intended for
 * valueFormatters, cell editors, and other non-React contexts. React cell
 * renderers that need reactive updates should subscribe via
 * `tradeDataStore((state) => state.tradeData)` and use findCPOnTrade instead.
 */
export function getCPFromStore(
  esid: string | undefined,
  executionRequestId: number | string | undefined,
): any | undefined {
  if (!esid) {
    return undefined;
  }
  const trade = tradeDataStore
    .getState()
    .tradeData.find((t: any) => t.esid === esid);
  return findCPOnTrade(trade, executionRequestId);
}

/**
 * Looks up the live CP from the Zustand tradeDataStore by esid + blockId.
 * Thin wrapper around findCPOnTradeByBlockId for non-React contexts.
 */
export function getCPFromStoreByBlockId(
  esid: string | undefined,
  blockId: number | string | undefined,
): any | undefined {
  if (!esid) {
    return undefined;
  }
  const trade = tradeDataStore
    .getState()
    .tradeData.find((t: any) => t.esid === esid);
  return findCPOnTradeByBlockId(trade, blockId);
}
